/**
 * Pop-out windows (docs/modules/messaging.md "Pop-out"): `openPopout(threadId)` opens a real
 * browser window with `window.open` and this window renders the thread into it through a React
 * portal, so the pop-out shares this window's session, query cache, SSE stream and sync object:
 * sending in the pop-out shows in the dock without a reload, and the browser holds one stream.
 * The route `/portal/messages/popout/$threadId` renders the same view standalone for a pop-out
 * that was reloaded or opened directly; it syncs through the BroadcastChannel.
 */
import { useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

type Popout = { threadId: string; win: Window; container: HTMLElement };

const popouts = new Map<string, Popout>();
const listeners = new Set<() => void>();
let hostMounted = 0;
let snapshot: Popout[] = [];

function emit() {
  snapshot = [...popouts.values()];
  for (const fn of listeners) fn();
}

export const POPOUT_FEATURES = 'popup,width=440,height=680';
export const popoutName = (threadId: string) => `oex-messages-${threadId}`;
export const popoutPath = (threadId: string) => `/portal/messages/popout/${threadId}`;

/** Copy the app's styles and theme classes into the new window. */
function prepare(win: Window, title: string): HTMLElement {
  const doc = win.document;
  doc.title = title;
  doc.documentElement.className = document.documentElement.className;
  for (const node of Array.from(document.head.querySelectorAll('style, link[rel="stylesheet"]'))) {
    doc.head.appendChild(doc.importNode(node, true));
  }
  doc.body.className = document.body.className;
  const container = doc.createElement('div');
  container.setAttribute('data-oex-popout', '');
  doc.body.appendChild(container);
  return container;
}

/**
 * Open (or focus) the pop-out of a thread. Returns how it was opened: `portal` (rendered from
 * this window), `route` (no host mounted: the standalone route), or `blocked`.
 */
export function openPopout(threadId: string, title = 'OEX Messages'): 'portal' | 'route' | 'blocked' {
  const existing = popouts.get(threadId);
  if (existing && !existing.win.closed) {
    existing.win.focus();
    return 'portal';
  }
  if (hostMounted === 0) {
    return window.open(popoutPath(threadId), popoutName(threadId), POPOUT_FEATURES) ? 'route' : 'blocked';
  }
  const win = window.open('', popoutName(threadId), POPOUT_FEATURES);
  if (!win) return 'blocked';
  const container = prepare(win, title);
  popouts.set(threadId, { threadId, win, container });
  const closeIt = () => closePopout(threadId);
  win.addEventListener('pagehide', closeIt);
  win.addEventListener('beforeunload', closeIt);
  emit();
  return 'portal';
}

export function closePopout(threadId: string): void {
  const p = popouts.get(threadId);
  if (!p) return;
  popouts.delete(threadId);
  try {
    if (!p.win.closed) p.win.close();
  } catch {
    // already gone
  }
  emit();
}

export function openPopouts(): readonly Popout[] {
  return snapshot;
}

/** Renders every open pop-out through a portal; mounted once, by the messaging dock. */
export function PopoutHost({ render }: { render(threadId: string, close: () => void): ReactNode }) {
  const list = useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      hostMounted += 1;
      return () => {
        listeners.delete(fn);
        hostMounted -= 1;
      };
    },
    () => snapshot,
    () => snapshot,
  );
  return (
    <>
      {list.map((p) =>
        createPortal(
          render(p.threadId, () => closePopout(p.threadId)),
          p.container,
          p.threadId,
        ),
      )}
    </>
  );
}

/** Test helper: forget every pop-out. */
export function resetPopouts(): void {
  popouts.clear();
  emit();
}
